import type { ChainFeedbackPayload } from './CombatFeedbackTypes';
import * as Phaser from 'phaser';
import { EventBus } from '../core/EventBus';
import { createLogger, Logger } from '../utils/Logger';
import { WeaponDamagePayload } from './WeaponTypes';

export interface StatusEffectSnapshot {
  readonly burn?: {
    readonly damagePerSecond: number;
    readonly expiresAt: number;
  };
  readonly slow?: {
    readonly multiplier: number;
    readonly expiresAt: number;
  };
}

export interface DamageResolutionHit {
  readonly enemy: Phaser.Physics.Arcade.Sprite;
  readonly kind: 'primary' | 'chain' | 'area';
  readonly damage: number;
  readonly remainingHealth: number;
  readonly maxHealth: number;
  readonly killed: boolean;
  readonly effects?: StatusEffectSnapshot;
}

export interface DamageResolution {
  readonly primary: DamageResolutionHit;
  readonly secondary: readonly DamageResolutionHit[];
  readonly critical: boolean;
  readonly criticalRoll: number;
}

export interface ResolveDamageContext {
  readonly projectile: Phaser.Physics.Arcade.Sprite;
  readonly primaryTarget: Phaser.Physics.Arcade.Sprite;
  readonly candidates: readonly Phaser.Physics.Arcade.Sprite[];
}

interface DamageResolverOptions {
  readonly rng?: () => number;
  readonly now?: () => number;
  readonly logger?: Logger;
}

const DEFAULT_CRIT_MULTIPLIER = 1.5;
const SECONDARY_HIT_COOLDOWN_MS = 500;

export class DamageResolver {
  private readonly rng: () => number;
  private readonly now: () => number;
  private readonly logger: Logger;

  constructor(options: DamageResolverOptions = {}) {
    this.rng = options.rng ?? Math.random;
    this.now = options.now ?? (() => Date.now());
    this.logger = options.logger ?? createLogger('DamageResolver');
  }

  public resolveHit(context: ResolveDamageContext): DamageResolution {
    const { projectile, primaryTarget, candidates } = context;
    const payload = this.readDamagePayload(projectile);
    const baseDamage = toPositiveNumber(projectile.getData('damage'), 0);

    if (baseDamage <= 0) {
      this.logger.error('resolveHit:malformed-damage-payload', {
        reason: 'non-positive-base-damage',
        baseDamage,
        payload
      });
    }

    const criticalRoll = this.rng();
    const critical = payload.critChance > 0 && criticalRoll <= payload.critChance;
    const primaryDamage = applyCritical(baseDamage, critical);
    const primaryEffects = this.buildStatusEffects(payload);
    const primary = this.applyDamageToEnemy(primaryTarget, primaryDamage, primaryEffects, 'primary');

    const secondaryHits: DamageResolutionHit[] = [];
    const hitTargets = new Set<Phaser.Physics.Arcade.Sprite>([primaryTarget]);

    if (payload.chainLightning && payload.chainLightning.maxTargets > 0) {
      const chainTargets = this.selectNearestTargets(
        primaryTarget,
        candidates,
        payload.chainLightning.maxTargets,
        payload.chainLightning.radius,
        hitTargets
      );

      chainTargets.forEach((target, index) => {
        if (!this.canApplySecondaryHit(projectile, target)) {
          return;
        }

        hitTargets.add(target);
        const chainDamage = Math.max(
          0,
          primaryDamage * (1 - ((payload.chainLightning?.falloff ?? 0) * (index + 1)))
        );
        const chainHit = this.applyDamageToEnemy(target, chainDamage, primaryEffects, 'chain');
        secondaryHits.push(chainHit);
        EventBus.getInstance().publish({ type: 'combat:chain', data: { x: primaryTarget.x, y: primaryTarget.y, toX: target.x, toY: target.y } satisfies ChainFeedbackPayload });
      });
    }

    const weaponType = String(projectile.getData('weaponType') ?? '');
    const areaRadius = toPositiveNumber(projectile.getData('areaRadius'), 0);
    if (weaponType === 'area' && payload.areaMultiplier > 0 && areaRadius > 0) {
      const areaTargets = candidates.filter(target => {
        if (!target.active || hitTargets.has(target)) return false;
        const distance = Phaser.Math.Distance.Between(projectile.x, projectile.y, target.x, target.y);
        return distance <= areaRadius;
      });

      areaTargets.forEach(target => {
        if (!this.canApplySecondaryHit(projectile, target)) {
          return;
        }

        hitTargets.add(target);
        const areaDamage = Math.max(0, primaryDamage * 0.5 * payload.areaMultiplier);
        const areaHit = this.applyDamageToEnemy(target, areaDamage, primaryEffects, 'area');
        secondaryHits.push(areaHit);
      });
    }

    return {
      primary,
      secondary: secondaryHits,
      critical,
      criticalRoll
    };
  }

  private readDamagePayload(projectile: Phaser.Physics.Arcade.Sprite): WeaponDamagePayload {
    const raw = projectile.getData('damagePayload') as Partial<WeaponDamagePayload> | undefined;
    if (!raw) {
      return {
        baseDamage: toPositiveNumber(projectile.getData('damage'), 0),
        bonusDamage: 0,
        areaMultiplier: 1,
        critChance: 0,
        chainHit: 0
      };
    }

    return {
      baseDamage: toPositiveNumber(raw.baseDamage, toPositiveNumber(projectile.getData('damage'), 0)),
      bonusDamage: toPositiveNumber(raw.bonusDamage, 0),
      areaMultiplier: toPositiveNumber(raw.areaMultiplier, 1),
      critChance: clamp(raw.critChance ?? 0, 0, 1),
      burn: raw.burn,
      slow: raw.slow,
      chainLightning: raw.chainLightning,
      chainHit: toPositiveNumber(raw.chainHit, 0)
    };
  }

  private applyDamageToEnemy(
    enemy: Phaser.Physics.Arcade.Sprite,
    damage: number,
    effects: StatusEffectSnapshot,
    kind: 'primary' | 'chain' | 'area'
  ): DamageResolutionHit {
    const normalizedDamage = Math.max(0, Math.round(damage * 100) / 100);
    const entity = enemy.getData('entity') as {
      takeDamage?: (amount: number) => number;
      getHealth?: () => number;
      getMaxHealth?: () => number;
      applyStatusEffects?: (effects: StatusEffectSnapshot) => void;
    } | undefined;

    let remainingHealth: number;
    let maxHealth: number;

    if (entity?.takeDamage) {
      remainingHealth = entity.takeDamage(normalizedDamage);
      maxHealth = entity.getMaxHealth?.() ?? toPositiveNumber(enemy.getData('maxHealth'), remainingHealth);
      enemy.setData('health', remainingHealth);
      enemy.setData('maxHealth', maxHealth);
    } else {
      maxHealth = toPositiveNumber(enemy.getData('maxHealth'), 100);
      const rawHealth = enemy.getData('health');
      const currentHealth = toPositiveNumber(rawHealth, maxHealth);
      if (rawHealth === undefined) {
        this.logger.warn('applyDamageToEnemy:missing-enemy-health-state', {
          enemyId: enemy.getData('id'),
          fallbackMaxHealth: maxHealth
        });
      }
      remainingHealth = Math.max(0, currentHealth - normalizedDamage);
      enemy.setData('health', remainingHealth);
    }

    if (entity?.applyStatusEffects) {
      entity.applyStatusEffects(effects);
    } else {
      if (effects.burn) {
        enemy.setData('burn', effects.burn);
      }
      if (effects.slow) {
        enemy.setData('slow', effects.slow);
      }
    }

    if (effects.burn || effects.slow) {
    }

    return {
      enemy,
      kind,
      damage: normalizedDamage,
      remainingHealth,
      maxHealth,
      killed: remainingHealth <= 0,
      effects
    };
  }

  private selectNearestTargets(
    origin: Phaser.Physics.Arcade.Sprite,
    candidates: readonly Phaser.Physics.Arcade.Sprite[],
    maxTargets: number,
    radius: number,
    excluded: Set<Phaser.Physics.Arcade.Sprite>
  ): Phaser.Physics.Arcade.Sprite[] {
    return candidates
      .filter(enemy => enemy.active && !excluded.has(enemy))
      .map(enemy => ({
        enemy,
        distance: Phaser.Math.Distance.Between(origin.x, origin.y, enemy.x, enemy.y)
      }))
      .filter(entry => entry.distance <= radius)
      .sort((left, right) => {
        return left.distance - right.distance;
      })
      .map(entry => entry.enemy)
      .slice(0, maxTargets);
  }

  private buildStatusEffects(payload: WeaponDamagePayload): StatusEffectSnapshot {
    const now = this.now();
    return {
      burn: payload.burn ? {
        damagePerSecond: payload.burn.damagePerSecond,
        expiresAt: now + (payload.burn.duration * 1000)
      } : undefined,
      slow: payload.slow ? {
        multiplier: payload.slow.multiplier,
        expiresAt: now + (payload.slow.duration * 1000)
      } : undefined
    };
  }

  private canApplySecondaryHit(projectile: Phaser.Physics.Arcade.Sprite, enemy: Phaser.Physics.Arcade.Sprite): boolean {
    const now = this.now();
    const existing = projectile.getData('secondaryHitCooldown') as
      | { enemy: Phaser.Physics.Arcade.Sprite; time: number }[]
      | undefined;
    const records = Array.isArray(existing) ? existing : [];

    const hitRecord = records.find(entry => entry.enemy === enemy);
    if (hitRecord && now - hitRecord.time < SECONDARY_HIT_COOLDOWN_MS) {
      return false;
    }

    if (hitRecord) {
      hitRecord.time = now;
    } else {
      records.push({ enemy, time: now });
    }

    projectile.setData(
      'secondaryHitCooldown',
      records.filter(entry => entry.enemy.active && now - entry.time < SECONDARY_HIT_COOLDOWN_MS)
    );
    return true;
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function toPositiveNumber(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : fallback;
}

function applyCritical(baseDamage: number, critical: boolean): number {
  if (!critical) return baseDamage;
  return baseDamage * DEFAULT_CRIT_MULTIPLIER;
}
